import { useEffect, useRef, useState } from "react";
import { Crosshair, LocateFixed, MapPin, X } from "lucide-react";
import { searchPlaces, type Place } from "@/lib/geocode";
import { MAP_COLORS } from "@/lib/map-colors";
import { cn } from "@/lib/utils";

export interface PointFieldHandlers {
  // Committed label for a set point ("Dropped pin", a place name, ...), or null.
  label: string | null;
  // This field's "choose on map" tap-to-place mode is active.
  picking: boolean;
  onSelectPlace: (coord: [number, number], label: string) => void;
  onChooseOnMap: () => void;
  onUseCurrentLocation: () => void;
  onClear: () => void;
}

interface PointFieldProps extends PointFieldHandlers {
  role: "start" | "end";
  // Bias place-search results toward this [lng, lat].
  bias?: [number, number];
}

// A checkered flag inside a circle, matching the end-point marker on the map.
// Same 14px outer circle as the start dot so both badges share a radius.
const CheckeredFlagBadge = () => (
  <span className="flex h-3.5 w-3.5 items-center justify-center overflow-hidden rounded-full border border-black bg-white">
    <svg
      width="14"
      height="14"
      viewBox="4 4 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 6h2v2H6zm2 2h2v2H8zm2-2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2 2h2v2h-2zM6 10h2v2H6zm2 2h2v2H8zm2-2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2 2h2v2h-2zM6 14h2v2H6zm2 2h2v2H8zm2-2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2 2h2v2h-2z"
        fill="black"
      />
    </svg>
  </span>
);

// One unified field per route point (Apple Maps directions pattern): type to
// search places, or pick "Choose on map" / "Current location" from the dropdown.
// While editing, the input shows the query; otherwise the committed label.
export const PointField = ({
  role,
  bias,
  label,
  picking,
  onSelectPlace,
  onChooseOnMap,
  onUseCurrentLocation,
  onClear,
}: PointFieldProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Place[]>([]);
  const [editing, setEditing] = useState(false);
  const seq = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Search only while the field is being edited.
  useEffect(() => {
    if (!editing) return;
    const id = ++seq.current;
    const timer = setTimeout(async () => {
      const places = await searchPlaces(query, bias);
      if (seq.current === id) setResults(places);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, bias, editing]);

  // Close the dropdown on any click outside the field.
  useEffect(() => {
    if (!editing) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setEditing(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editing]);

  const dot =
    role === "start" ? MAP_COLORS.suggestedStart : MAP_COLORS.suggestedEnd;
  const placeholder = picking
    ? `Tap the map to set ${role}`
    : role === "start"
      ? "Search start point"
      : "Search end point (optional)";

  const startEditing = () => {
    setEditing(true);
    setQuery("");
    setResults([]);
  };

  const pick = (fn: () => void) => {
    fn();
    setEditing(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border bg-white px-2 shadow-sm",
          picking
            ? "border-stone-400 ring-1 ring-stone-300"
            : "border-stone-200",
        )}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          {role === "start" ? (
            <span
              className="h-3.5 w-3.5 rounded-full"
              style={{ backgroundColor: dot }}
            />
          ) : (
            <CheckeredFlagBadge />
          )}
        </span>
        <input
          value={editing ? query : (label ?? "")}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={startEditing}
          className="h-9 w-full bg-transparent text-xs outline-none placeholder:text-stone-400"
        />
        {label && !editing && (
          <button
            type="button"
            aria-label={`Clear ${role} point`}
            onClick={onClear}
            className="shrink-0 rounded-full p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {editing && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-stone-200 bg-white py-1 shadow-lg">
          <li>
            <button
              type="button"
              onClick={() => pick(onChooseOnMap)}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-50"
            >
              <Crosshair className="h-3.5 w-3.5 text-stone-500" />
              Choose on map
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => pick(onUseCurrentLocation)}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-50"
            >
              <LocateFixed className="h-3.5 w-3.5 text-stone-500" />
              Current location
            </button>
          </li>
          {results.length > 0 && (
            <li className="my-1 border-t border-stone-100" aria-hidden />
          )}
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => pick(() => onSelectPlace(r.coord, r.label))}
                className="flex w-full items-start gap-2 px-2 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-50"
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
